import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from './api';
import GameCard from './components/GameCard';
import BestBetCard from './components/BestBetCard';
import RecalculatingState from './components/RecalculatingState';
import { useAvailableSports } from './hooks/useAvailableSports';

import { Link, useNavigate } from 'react-router-dom'; // Ensure Link is imported
import { useAuth } from './contexts/AuthContext'; // Import useAuth to get subscription status
import { useFeatureAccess, useSubscription } from './contexts/SubscriptionContext';
import { ENTITLEMENT_KEYS } from './config/entitlements';
import { buildLockedOverlay, shouldLockPreviewItem } from './services/featureGateService';

import './HomePage.css';
import PageSeo from './components/PageSeo';
import { getPageSeo } from './utils/seoConfig';
import { getStateContent } from './config/stateContent';
import { getStatePlatforms } from './config/sportsbookCatalog';

// ── Helpers for BestBetCard ───────────────────────────────────────────────────
// Wave bar heights for Live Scanner Chip waveform
const LSC_WAVE_PEAKS = [5, 9, 13, 7, 15, 10, 5, 12, 8, 14, 6, 10, 13, 5, 8, 11, 7, 14, 9, 12, 6, 15, 8, 11, 5, 13, 10, 7, 14, 8, 12, 6, 9, 15, 7, 11];

const HP_BOOKMAKER_ICONS = {
  'DraftKings':         '/images/draftkings_icon.png',
  'FanDuel':            '/images/fanduel_icon.png',
  'BetMGM':             '/images/bet_mgm_icon.png',
  'Caesars':            '/images/caesars_icon.png',
  'Hard Rock Bet':      '/images/hard_rock_bet_icon.png',
  'Hard Rock Bet (AZ)': '/images/hard_rock_bet_icon.png',
  'Hard Rock Bet (FL)': '/images/hard_rock_bet_icon.png',
  'Hard Rock Bet (OH)': '/images/hard_rock_bet_icon.png',
  'ESPN BET':           '/images/espn_bet_icon.png',
  'theScore Bet':       '/images/espn_bet_icon.png',
  'BetRivers':          '/images/betrivers_icon.png',
  'Fanatics':           '/images/fanatics_icon.png',
  'Bally Bet':          '/images/bally_bet_icon.png',
  'BetParx':            '/images/betparx.jpeg',
  'BetUS':              '/images/betus.jpeg',
  'Bovada':             '/images/bovada.webp',
  'LowVig.ag':          '/images/lowvig.jpg',
  'Novig':              '/images/novig.png',
  'ReBet':              '/images/rebet.jpg',
  'Betr DFS':           '/images/betr.jpg',
  'DraftKings Pick6':   '/images/pick6.png',
  'PrizePicks':         '/images/prizepicks.jpg',
  'Underdog':           '/images/underdog.png',
  'Kalshi':             '/images/kalshi.png',
  'Polymarket':         '/images/polymarket.png',
  'ProphetX':           '/images/prophetx.png',
};

function hpFormatMarket(market) {
  if (!market) return '';
  let s = market.startsWith('player_') ? market.slice('player_'.length) : market;
  if (s.startsWith('batter_')) s = s.slice('batter_'.length);
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function hpFmtOdds(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '–';
  return n > 0 ? `+${n}` : `${n}`;
}

// Converts a win-probability % to American odds (null if invalid)
function probToAmericanOdds(probPct) {
  const p = Number(probPct);
  if (!probPct || !Number.isFinite(p) || p <= 0 || p >= 1) return null;
  const dec = 1 / p;
  return dec >= 2 ? Math.round((dec - 1) * 100) : Math.round(-100 / (dec - 1));
}

// ── Tier-3 static fallback — shown when no live positive-EV bet is available ──
// Realistic NBA moneyline example demonstrating the panel's full feature set.
const STATIC_FEATURED_BET = {
  sport:        'basketball_nba',
  market:       'h2h',
  player:       null,
  team:         'Boston Celtics',
  home_team:    'Miami Heat',
  away_team:    'Boston Celtics',
  bet_type:     null,
  point_line:   null,
  commence_time: null,
  // Tier 3 resolves from these directly
  expected_value: 0.052,
  price:          -108,
  bookmaker:      'DraftKings',
  p_true:         0.538,
  bookmaker_prices: {
    'DraftKings': -108,
    'FanDuel':    -112,
    'BetMGM':     -115,
    'Caesars':    -110,
    'BetRivers':  -118,
    'Fanatics':   -113,
  },
  // Aliases used by tier-2 resolution path (unused for tier 3 but kept consistent)
  ev_best:        0.052,
  best_price:     -108,
  best_bookmaker: 'DraftKings',
};

const PRIORITY_SPORTS = [
  'americanfootball_nfl',
  'basketball_nba',
  'americanfootball_ncaaf',
  'soccer_fifa_world_cup',
  'icehockey_nhl',
  'baseball_mlb',
  'basketball_wnba',
  'mma_mixed_martial_arts',
  'soccer_usa_mls',
  'soccer_epl',
  'soccer_france_ligue_one',
  'soccer_germany_bundesliga',
  'soccer_spain_la_liga',
  'soccer_italy_serie_a',
];

// ─── Soccer grouping ────────────────────────────────────────────────────────
// All soccer_* sport codes are consolidated into a single "⚽ Soccer" entry in
// the primary sport filter. Selecting it activates a secondary filter bar with
// the individual soccer leagues, derived dynamically from whatever soccer_*
// codes the API currently reports (no hardcoded league list). `selectedHomeSport`
// remains the single source of truth: it holds either the sentinel group
// value (combined view) or a specific soccer_* code (one league picked from
// the secondary bar) — no parallel filtering state is introduced.
const SOCCER_SPORT_PREFIX = 'soccer_';
const SOCCER_GROUP_VALUE = 'soccer';

function isSoccerSport(sport) {
  return typeof sport === 'string' && sport.startsWith(SOCCER_SPORT_PREFIX);
}

// Sport-tab cache TTL: entries older than this threshold are re-fetched.
// Prevents a single partial/stale response from poisoning the cache for the
// entire browser session.
const SPORT_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const SPORT_LABELS = {
  [SOCCER_GROUP_VALUE]: '⚽ Soccer',
  americanfootball_nfl: 'NFL',
  basketball_nba: 'NBA',
  americanfootball_ncaaf: 'NCAAF',
  icehockey_nhl: 'NHL',
  baseball_mlb: 'MLB',
  basketball_wnba: 'WNBA',
  soccer_fifa_world_cup: 'FIFA World Cup',
  mma_mixed_martial_arts: 'MMA',
  baseball_milb: 'Minor League Baseball',
  baseball_ncaa: 'College Baseball',
  basketball_ncaab: 'NCAA Basketball',
  soccer_usa_mls: 'MLS',
  soccer_epl: 'Premier League',
  soccer_france_ligue_one: 'Ligue 1',
  soccer_germany_bundesliga: 'Bundesliga',
  soccer_spain_la_liga: 'La Liga',
  soccer_italy_serie_a: 'Serie A',

};

const BOOKMAKER_TITLE_BY_KEY = {
  draftkings: 'DraftKings',
  fanduel: 'FanDuel',
  betmgm: 'BetMGM',
  williamhill_us: 'Caesars',
  caesars: 'Caesars',
  hardrockbet: 'Hard Rock Bet',
  hardrockbet_az: 'Hard Rock Bet (AZ)',
  hardrockbet_fl: 'Hard Rock Bet (FL)',
  hardrockbet_oh: 'Hard Rock Bet (OH)',
  espnbet: 'ESPN BET',
  thescorebet: 'theScore Bet',
  betrivers: 'BetRivers',
  fanatics: 'Fanatics',
  ballybet: 'Bally Bet',
  betparx: 'BetParx',
  betus: 'BetUS',
  bovada: 'Bovada',
  lowvig: 'LowVig.ag',
  novig: 'Novig',
  rebet: 'ReBet',
  betr_us_dfs: 'Betr DFS',
  pick6: 'DraftKings Pick6',
  prizepicks: 'PrizePicks',
  underdog: 'Underdog',
  kalshi: 'Kalshi',
  polymarket: 'Polymarket',
  prophetx: 'ProphetX',
};

function getBookmakerTitle(value) {
  if (!value) return null;
  return BOOKMAKER_TITLE_BY_KEY[String(value).toLowerCase()] ?? value;
}

function resolveDisplayedMarketFields(item, activeState, activeBookmaker) {
  const bookmakerTitle = getBookmakerTitle(activeBookmaker);

  if (bookmakerTitle && item?.bookmaker_price != null) {
    return {
      expected_value: item.bookmaker_ev,
      price: item.bookmaker_price,
      bookmaker: bookmakerTitle,
    };
  }

  if (activeState && item?.state_best_price != null) {
    return {
      expected_value: item.state_ev,
      price: item.state_best_price,
      bookmaker: item.state_best_bookmaker,
    };
  }

  return {
    expected_value: item?.ev_best,
    price: item?.best_price,
    bookmaker: item?.best_bookmaker,
  };
}

function normalizeMarketRows(rows, game, activeState, activeBookmaker) {
  if (!Array.isArray(rows)) return [];

  const mapped = rows.map(item => {
    const displayFields = resolveDisplayedMarketFields(item, activeState, activeBookmaker);
    return {
      ...item,
      sport: item?.sport ?? game?.sport ?? null,
      game_id: item?.game_id ?? game?.game_id ?? null,
      home_team: item?.home_team ?? game?.home_team ?? null,
      away_team: item?.away_team ?? game?.away_team ?? null,
      commence_time: item?.commence_time ?? game?.commence_time ?? null,
      expected_value: displayFields.expected_value,
      price: displayFields.price,
      bookmaker: displayFields.bookmaker,
    };
  });

  const filtered = mapped.filter(item => item.price != null);

  // Warn when rows are silently dropped due to null price — this is the most
  // common cause of missing market sections in GameCard.
  if (process.env.NODE_ENV !== 'production' && filtered.length < mapped.length) {
    console.warn(
      `[normalizeMarketRows] Dropped ${mapped.length - filtered.length} row(s) with null price`,
      { market: rows[0]?.market, activeBookmaker, activeState, gameId: game?.game_id }
    );
  }

  return filtered;
}

function normalizeGameOverviewResponse(responseGames, activeState, activeBookmaker) {
  if (!Array.isArray(responseGames)) return [];

  return responseGames
    .map(game => ({
      gameId: game?.game_id,
      sport: game?.sport ?? null,
      homeTeam: game?.home_team ?? '',
      awayTeam: game?.away_team ?? '',
      commenceTime: game?.commence_time ?? null,
      markets: {
        h2h: normalizeMarketRows(game?.markets?.h2h, game, activeState, activeBookmaker),
        spreads: normalizeMarketRows(game?.markets?.spreads, game, activeState, activeBookmaker),
        totals: normalizeMarketRows(game?.markets?.totals, game, activeState, activeBookmaker),
        props: game?.markets?.props ?? {},
      },
    }))
    .filter(game => game.gameId)
    .sort((a, b) => {
      const ta = a.commenceTime ? new Date(a.commenceTime).getTime() : Number.POSITIVE_INFINITY;
      const tb = b.commenceTime ? new Date(b.commenceTime).getTime() : Number.POSITIVE_INFINITY;
      return ta - tb;
    });
}

// ── Query functions (used by useQuery below) ────────────────────────────────
async function fetchHomeBestBets({ userState, selectedBookmaker, signal }) {
  // Fetch more items when state-sorted so Tier 2 (global) has sufficient candidates
  const params = { limit: userState ? 8 : 4 };
  if (userState) {
    params.sort_by = 'ev_state';
    params.state   = userState;
  } else {
    params.sort_by = 'ev_best';
  }
  if (selectedBookmaker) params.bookmaker = selectedBookmaker;

  const res = await api.get('best-bets/', { params, signal });
  const results = Array.isArray(res.data?.results) ? res.data.results : [];

  const normalized = results
    .map(item => {
      const displayFields = resolveDisplayedMarketFields(item, userState, selectedBookmaker);
      return {
        ...item,
        expected_value: displayFields.expected_value,
        price:          displayFields.price,
        bookmaker:      displayFields.bookmaker,
      };
    })
    .filter(item => item.price != null);

  // EV count: prefer state-scoped count, fall back to global
  const stateCount = res.data?.positive_ev_state_count;
  const bestCount  = res.data?.positive_ev_best_count;
  const evCount = userState && stateCount != null ? stateCount : (bestCount ?? null);

  return { results, normalized, evCount };
}

async function fetchHomeUpcomingGames({ userState, selectedBookmaker, signal }) {
  const params = { mode: 'upcoming' };
  if (userState) params.state = userState;
  if (selectedBookmaker) params.bookmaker = selectedBookmaker;

  const res = await api.get('games/market-overview/', { params, signal });
  return normalizeGameOverviewResponse(res.data?.games, userState, selectedBookmaker);
}

async function fetchHomeSportGames({ selectedHomeSport, userState, selectedBookmaker, soccerSubSports, signal }) {
  // Combined "All Soccer" view — the backend only supports filtering by one
  // exact sport code, not a group/prefix, so fetch every known soccer_*
  // league in parallel and merge the results (games listings are exhaustive
  // per league, so no over-fetch/approximation is needed here).
  if (selectedHomeSport === SOCCER_GROUP_VALUE) {
    if (soccerSubSports.length === 0) return [];

    const responses = await Promise.all(
      soccerSubSports.map(league => {
        const leagueParams = { sport: league };
        if (userState) leagueParams.state = userState;
        if (selectedBookmaker) leagueParams.bookmaker = selectedBookmaker;
        return api.get('games/market-overview/', { params: leagueParams, signal }).catch(() => null);
      })
    );

    const mergedRawGames = responses.flatMap(res =>
      Array.isArray(res?.data?.games) ? res.data.games : []
    );
    return normalizeGameOverviewResponse(mergedRawGames, userState, selectedBookmaker);
  }

  // MLB datasets are large and frequently cause timeouts at the default 10s.
  // Use an extended timeout and fall back to 'baseball_milb' if the primary
  // key returns an empty result set.
  const isMLB = selectedHomeSport === 'baseball_mlb';
  const requestConfig = isMLB ? { timeout: 30_000, signal } : { signal };

  const params = { sport: selectedHomeSport };
  if (userState) params.state = userState;
  if (selectedBookmaker) params.bookmaker = selectedBookmaker;

  let res = await api.get('games/market-overview/', { params, ...requestConfig });

  // MLB fallback: if baseball_mlb returned no games, retry with baseball_milb
  if (isMLB && (!Array.isArray(res.data?.games) || res.data.games.length === 0)) {
    const fallbackParams = { ...params, sport: 'baseball_milb' };
    const fallbackRes = await api.get('games/market-overview/', { params: fallbackParams, ...requestConfig });
    if (Array.isArray(fallbackRes.data?.games) && fallbackRes.data.games.length > 0) {
      res = fallbackRes;
    }
  }

  return normalizeGameOverviewResponse(res.data?.games, userState, selectedBookmaker);
}

export default function HomePage({userState}) {
  const { user, loading: authLoading } = useAuth(); // Get user and auth loading state
  const bestBetsAccess = useFeatureAccess(ENTITLEMENT_KEYS.BEST_BETS_FULL);
  const navigate = useNavigate();

  // Raw sport keys reported by the API (pre soccer-grouping). Tennis entries
  // are relabeled to display strings below (see `knownSports`).
  const { sportKeys: rawSportKeys } = useAvailableSports();
  const [selectedBookmaker, setSelectedBookmaker] = useState(
    () => localStorage.getItem('selectedBookmaker') || ''
  );

  // ── Best-bets query (EV counter + Top Free Picks) ─────────────────────────
  const bestBetsQuery = useQuery({
    queryKey: ['home-best-bets', userState, selectedBookmaker],
    queryFn: ({ signal }) => fetchHomeBestBets({ userState, selectedBookmaker, signal }),
  });
  // Raw (un-normalized) results — used for tiered featured-bet selection
  const rawBestBets     = bestBetsQuery.data?.results ?? [];
  const bestBets         = bestBetsQuery.data?.normalized ?? [];
  const bestBetsLoading = bestBetsQuery.isLoading;
  const evCount          = bestBetsQuery.data?.evCount ?? null;

  const premiumBestBetsOverlay = useMemo(
    () => buildLockedOverlay(ENTITLEMENT_KEYS.BEST_BETS_FULL, {
      description: 'Subscribe to access every top bet and real-time updates.',
    }),
    []
  );

  // Use SubscriptionContext so the admin override is respected.
  // When override is active, subscription.isActive is true regardless of the
  // real user profile — this is the single source of truth for access checks.
  const { subscription } = useSubscription();
  const isSubscribed = subscription.isActive;

  // ── New homepage sport filter ────────────────────────────────────────────
  const [selectedHomeSport, setSelectedHomeSport] = useState('upcoming');

  useEffect(() => {
    const syncBookmaker = () => {
      setSelectedBookmaker(localStorage.getItem('selectedBookmaker') || '');
    };

    window.addEventListener('storage', syncBookmaker);
    window.addEventListener('focus', syncBookmaker);
    return () => {
      window.removeEventListener('storage', syncBookmaker);
      window.removeEventListener('focus', syncBookmaker);
    };
  }, []);

  // Tennis entries are relabeled to display strings here, mirroring the
  // pre-existing fetch-time transform this replaced.
  const knownSports = useMemo(
    () => rawSportKeys.map(sport => {
      const lowerSport = sport.toLowerCase();
      if (lowerSport.includes('tennis_atp')) return "Men's Tennis";
      if (lowerSport.includes('wta')) return "Women's Tennis";
      return sport;
    }),
    [rawSportKeys]
  );

  // Primary sport filter list — all soccer_* codes collapsed into a single
  // SOCCER_GROUP_VALUE entry, keeping the same priority-then-alphabetical
  // ordering the API-driven list always used.
  const availableSports = useMemo(() => {
    let soccerGroupAdded = false;
    const priorityOrdered = [];
    for (const s of PRIORITY_SPORTS) {
      if (isSoccerSport(s)) {
        if (!soccerGroupAdded && knownSports.some(isSoccerSport)) {
          priorityOrdered.push(SOCCER_GROUP_VALUE);
          soccerGroupAdded = true;
        }
        continue;
      }
      if (knownSports.includes(s)) priorityOrdered.push(s);
    }
    const rest = knownSports
      .filter(s => !isSoccerSport(s) && !PRIORITY_SPORTS.includes(s))
      .sort();
    return [...priorityOrdered, ...rest];
  }, [knownSports]);

  // Individual soccer leagues for the secondary filter bar — derived
  // dynamically from whatever soccer_* sports the API currently reports.
  const soccerSubSports = useMemo(
    () => knownSports
      .filter(isSoccerSport)
      .sort((a, b) => (SPORT_LABELS[a] ?? a).localeCompare(SPORT_LABELS[b] ?? b)),
    [knownSports]
  );

  const isSoccerModeActive = selectedHomeSport === SOCCER_GROUP_VALUE || isSoccerSport(selectedHomeSport);

  // ── Upcoming grouped games query ──────────────────────────────────────────
  const upcomingGamesQuery = useQuery({
    queryKey: ['home-upcoming-games', userState, selectedBookmaker],
    queryFn: ({ signal }) => fetchHomeUpcomingGames({ userState, selectedBookmaker, signal }),
  });
  const upcomingGames        = upcomingGamesQuery.data ?? [];
  const upcomingGamesLoading = upcomingGamesQuery.isLoading;

  // ── Selected-tab sport games query — cached per sport/state/bookmaker combo,
  //    replacing the old hand-rolled Map cache with React Query's own cache. ──
  const sportGamesQuery = useQuery({
    queryKey: ['home-sport-games', selectedHomeSport, userState, selectedBookmaker, soccerSubSports],
    queryFn: ({ signal }) => fetchHomeSportGames({ selectedHomeSport, userState, selectedBookmaker, soccerSubSports, signal }),
    enabled: selectedHomeSport !== 'upcoming',
    staleTime: SPORT_CACHE_TTL_MS,
  });
  const selectedSportGames        = sportGamesQuery.data ?? [];
  const selectedSportGamesLoading = sportGamesQuery.isLoading;

  // Use player with team as fallback so h2h bets that store the team name in
  // `team` (not `player`) still produce a unique, stable key.
  const betKey = bet =>
    `${bet.game_id || 'game'}-${bet.player || bet.team || ''}-` +
    `${bet.market || 'market'}-${bet.bet_type || ''}-${bet.point_line ?? ''}-${bet.bookmaker || ''}`;

  // ── Featured Bet: 3-tier fallback for hero panel ──────────────────────────
  // Tier 1 — state_ev > 0       (requires userState; uses state-scoped price)
  // Tier 2 — ev_best > 0        (global fallback; same fetch, wider candidate pool)
  // Tier 3 — STATIC_FEATURED_BET (panel is never empty after loading completes)
  const { featuredRawItem, featuredTier } = useMemo(() => {
    // Keep skeleton visible while the fetch is in flight
    if (bestBetsLoading) return { featuredRawItem: null, featuredTier: null };

    const EXCLUDED = ['soccer_', 'cricket_'];
    const notExcluded = item => !EXCLUDED.some(ex => (item?.sport || '').startsWith(ex));

    // Tier 1: best state-scoped positive-EV bet
    if (userState) {
      const t1 = rawBestBets.find(item =>
        notExcluded(item) &&
        item.state_best_price != null &&
        Number(item.state_ev) > 0
      );
      if (t1) return { featuredRawItem: t1, featuredTier: 1 };
    }

    // Tier 2: best global positive-EV bet
    const t2 = rawBestBets.find(item =>
      notExcluded(item) &&
      item.best_price != null &&
      Number(item.ev_best) > 0
    );
    if (t2) return { featuredRawItem: t2, featuredTier: 2 };

    // Tier 3: static example — always resolves, title changes to "Odds Analysis Example"
    return { featuredRawItem: STATIC_FEATURED_BET, featuredTier: 3 };
  }, [rawBestBets, userState, bestBetsLoading]);

  // ── Pre-computed display values for the hero EV intelligence panel ───────
  const featuredBetDisplay = useMemo(() => {
    if (!featuredRawItem) return null;

    // Resolve price / EV / bookmaker based on active tier
    let resolvedPrice, resolvedEv, resolvedBookmaker;
    if (featuredTier === 1) {
      resolvedPrice     = featuredRawItem.state_best_price;
      resolvedEv        = featuredRawItem.state_ev;
      resolvedBookmaker = featuredRawItem.state_best_bookmaker;
    } else if (featuredTier === 2) {
      resolvedPrice     = featuredRawItem.best_price;
      resolvedEv        = featuredRawItem.ev_best;
      resolvedBookmaker = featuredRawItem.best_bookmaker;
    } else {
      // Tier 3: static fallback fields are already resolved
      resolvedPrice     = featuredRawItem.price;
      resolvedEv        = featuredRawItem.expected_value;
      resolvedBookmaker = featuredRawItem.bookmaker;
    }

    const evPct = Number(resolvedEv) * 100;

    // Fair odds from win probability
    const fairOdds = probToAmericanOdds(featuredRawItem.p_true);

    // Sport label
    const SPORT_MAP = {
      basketball_nba: 'NBA', americanfootball_nfl: 'NFL', baseball_mlb: 'MLB',
      icehockey_nhl: 'NHL', basketball_wnba: 'WNBA',
    };
    const sportLabel = SPORT_MAP[featuredRawItem.sport] ?? featuredRawItem.sport?.split('_').pop().toUpperCase() ?? '';

    // Market label
    const MKTMAP = { h2h: 'Moneyline', spreads: 'Spread', totals: 'Total' };
    const marketLabel = MKTMAP[featuredRawItem.market] || hpFormatMarket(featuredRawItem.market);

    // Game time
    const gameTime = featuredRawItem.commence_time
      ? new Date(featuredRawItem.commence_time).toLocaleString('en-US', {
          weekday: 'short', month: 'short', day: 'numeric',
          hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
        })
      : null;

    // Bet title
    let betTitle;
    if (featuredRawItem.player) {
      betTitle = `${featuredRawItem.player} ${featuredRawItem.bet_type} ${featuredRawItem.point_line ?? ''}`.trim();
    } else if (featuredRawItem.market === 'h2h') {
      betTitle = `${featuredRawItem.team || featuredRawItem.away_team || ''} Moneyline`.trim();
    } else if (featuredRawItem.market === 'spreads') {
      betTitle = `${featuredRawItem.team || featuredRawItem.away_team || ''} ${featuredRawItem.bet_type} ${featuredRawItem.point_line ?? ''}`.trim();
    } else if (featuredRawItem.market === 'totals') {
      betTitle = `${featuredRawItem.away_team} @ ${featuredRawItem.home_team} ${featuredRawItem.bet_type} ${featuredRawItem.point_line ?? ''}`.trim();
    } else {
      betTitle = `${featuredRawItem.away_team || ''} @ ${featuredRawItem.home_team || ''}`.trim();
    }

    // Sportsbook comparison (sorted best→worst, capped at 8)
    const bookEntries = featuredRawItem.bookmaker_prices
      ? Object.entries(featuredRawItem.bookmaker_prices)
          .filter(([, p]) => p != null && Number.isFinite(Number(p)))
          .map(([name, price]) => ({
            name,
            price: Number(price),
            logo: HP_BOOKMAKER_ICONS[name] ?? null,
            isBest: name === resolvedBookmaker,
          }))
          .sort((a, b) => b.price - a.price)
          .slice(0, 8)
      : [];

    const prices = bookEntries.map(b => b.price);
    const minP  = prices.length > 0 ? Math.min(...prices) : 0;
    const maxP  = prices.length > 0 ? Math.max(...prices) : 0;
    const range = maxP - minP;

    return {
      tier: featuredTier,
      evPct,
      fairOdds,
      sportLabel,
      marketLabel,
      gameTime,
      betTitle,
      bookEntries,
      minP,
      maxP,
      range,
      bestPrice:    resolvedPrice,
      bestBook:     resolvedBookmaker,
      bestBookLogo: HP_BOOKMAKER_ICONS[resolvedBookmaker] ?? null,
    };
  }, [featuredRawItem, featuredTier]);

  // Games for the homepage rail come directly from the grouped backend response.
  const homeSportGames = useMemo(() => {
    return selectedHomeSport === 'upcoming' ? upcomingGames : selectedSportGames;
  }, [selectedHomeSport, upcomingGames, selectedSportGames]);

  // ── State-specific platform section (sportsbooks / DFS / trading markets) ─
  const stateName = useMemo(
    () => getStateContent(userState)?.stateName || userState || 'Your State',
    [userState]
  );
  const statePlatforms = useMemo(() => getStatePlatforms(userState), [userState]);
  const stateSlug = (userState || '').toLowerCase();
  const PLATFORM_CATEGORY_SECTIONS = [
    {
      key: 'trading',
      title: 'Trading Markets',
      items: statePlatforms.trading,
      info: 'Trading markets like Kalshi and Novig let you trade "yes/no" contracts or wager peer-to-peer against other users instead of a traditional sportsbook. Prices move with the market itself, which can create different value than fixed sportsbook odds.',
    },
    {
      key: 'dfs',
      title: 'DFS Apps',
      items: statePlatforms.dfs,
      info: "Daily fantasy sports (DFS) and pick'em apps like PrizePicks and Underdog let you build entries by picking whether a player goes over or under a projected stat line, using entry fees and payout multipliers instead of standard betting odds.",
    },
    {
      key: 'sportsbooks',
      title: 'Sportsbooks',
      items: statePlatforms.sportsbooks,
      info: 'Traditional sportsbooks let you wager on moneylines, spreads, totals, and player props at fixed odds. Odds can vary between sportsbooks, which is why comparing sportsbook odds before you bet matters.',
    },
  ];

  const showSkeletons = bestBetsLoading;
  const showGameCardSkeletons = (selectedHomeSport === 'upcoming'
    ? upcomingGamesLoading
    : selectedSportGamesLoading);

  // SEO is handled declaratively via <PageSeo> in the JSX return below.

  return (
    <>
      <PageSeo seo={getPageSeo('home', userState)} />
      <div className="home-page">
        
        {/* ─────────────────────────────────────────────────────────────────
            HERO V2 — Bloomberg Terminal × EV SaaS
            Premium dark UI · glowing green accents · cinematic motion
        ───────────────────────────────────────────────────────────────── */}
        <section className="hero-v2">

          {/* Multi-layer animated background */}
          <div className="hv2-bg" aria-hidden="true">
            <div className="hv2-bg__grid" />
            <div className="hv2-bg__glow hv2-bg__glow--1" />
            <div className="hv2-bg__glow hv2-bg__glow--2" />
            <div className="hv2-bg__noise" />
          </div>

          <div className="hv2-hero-compact">

              {/* Main headline — state-aware, with live state selector */}
              <h1 className="hv2-headline">
                
                <span className="hv2-headline__copy">
                  Data-Driven
                  <span className="hv2-headline__em">
                    Sports Betting
                  </span>
                </span>
                <div
                  className="lsc-chip"
                  aria-label={`${bestBetsLoading || evCount == null ? 'Loading' : evCount} positive EV bets found today`}
                >
                  <div className="lsc-sweep" aria-hidden="true" />
                  <div className="lsc-indicator" aria-hidden="true">
                    <span className="lsc-pulse-ring" />
                    <span className="lsc-pulse-dot" />
                  </div>
                  <div className="lsc-body">
                    <div className="lsc-row-top">
                      <span className="lsc-tag">LIVE</span>
                      <span className="lsc-sys-label">
                        {userState && (
                          <>
                            <span className="lsc-sys-label__state lsc-sys-label__state--full">{stateName.toUpperCase()}</span><span className="lsc-sys-label__state lsc-sys-label__state--abbr">{userState.toUpperCase()}</span>{' '}
                          </>
                        )}
                        MARKET SCANNER
                      </span>
                    </div>
                    <div className="lsc-count-line">
                      <span className="lsc-count" aria-live="polite">
                        {bestBetsLoading ? '—' : evCount != null ? evCount : '—'}
                      </span>
                      <Link
                        to="/academy/ev-betting"
                        className="lsc-count-desc lsc-count-desc--link"
                        aria-label="Learn what +EV means — opens the Expected Value Betting lesson"
                        title="Learn what +EV means"
                      >
                        <span className="lsc-count-desc__ev">+EV</span>
                      </Link>
                      <span className="lsc-count-desc lsc-count-desc--plain">bets found</span>
                    </div>
                    <div className="lsc-hook-line">Scanning 20+ sportsbooks in real time</div>
                    <div className="lsc-wave" aria-hidden="true">
                      {LSC_WAVE_PEAKS.map((peak, i) => (
                        <span
                          key={i}
                          className="lsc-wave__bar"
                          style={{ '--peak': `${peak}px`, '--i': i }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </h1>

              {/* Live Scanner + hook copy */}
              <div className="hv2-scanner-row">
                <p className="hv2-hero-hook">
                  The Best Odds Don't Last Forever.
                  <span className="hv2-hero-hook__sub">
                    Market inefficiencies don't last long. We continuously monitor 20+ sportsbooks to help you find the best available odds before they move.
                  </span>
                </p>
              </div>

        
              


          </div>
        </section>
        
        {/* OLD: Performance Banner Section - Commented Out
        {performanceBanner && (
          <section className="performance-banner-section">
            <div className="banner-section-header">
              <h2 className="banner-section-title">📊 Live Performance Results</h2>
              <p className="banner-section-subtitle">Automatically calculated ROI and win rates</p>
            </div>
            <div className="performance-banner-container">
              <div className="performance-banner-primary">
                {performanceBanner.messages.primary}
              </div>
              
              {performanceBanner.top_3_positions && performanceBanner.top_3_positions.length > 0 && (
                <div className="top-positions-container">
                  <h3 className="top-positions-title">🏅 Top 3 Positions by ROI</h3>
                  <div className="top-positions-grid">
                    {performanceBanner.top_3_positions.map((pos, idx) => (
                      <div key={idx} className={`position-card position-card-rank-${pos.rank}`}>
                        <div className="position-card-header">
                          <span className="position-emoji">{pos.emoji}</span>
                          <span className="position-rank">#{pos.rank}</span>
                        </div>
                        <div className="position-card-title">
                          {pos.position_full_name || pos.position} {pos.bet_type}
                        </div>
                        <div className="position-card-stats">
                          <div className="position-stat">
                            <span className="position-stat-label">Record</span>
                            <span className="position-stat-value">{pos.record}</span>
                          </div>
                          <div className="position-stat">
                            <span className="position-stat-label">Win Rate</span>
                            <span className="position-stat-value">{pos.win_rate}%</span>
                          </div>
                          <div className="position-stat position-stat-roi">
                            <span className="position-stat-label">ROI</span>
                            <span className="position-stat-value">+{pos.roi}%</span>
                          </div>
                          <div className="position-stat">
                            <span className="position-stat-label">Profit</span>
                            <span className="position-stat-value">{pos.profit}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {performanceBanner.updated_at && (
                <div className="performance-banner-timestamp">
                  Last updated: {new Date(performanceBanner.updated_at).toLocaleString()}
                </div>
              )}
            </div>
          </section>
        )}
        */}

        {/* Top 4 Bets Section */}
        <section className="premium-picks-section">

          {/* ── Cinematic background system (aria-hidden, purely decorative) ── */}
          <div className="ppbg" aria-hidden="true">
            {/* Layer 2: Aurora gradient blobs */}
            <div className="ppbg__aurora ppbg__aurora--1" />
            <div className="ppbg__aurora ppbg__aurora--2" />
            <div className="ppbg__aurora ppbg__aurora--3" />
            <div className="ppbg__aurora ppbg__aurora--4" />
            {/* Layer 3: Cinematic grain/noise */}
            <div className="ppbg__noise" />
            {/* Layer 4: Data grid overlay */}
            <div className="ppbg__grid" />
            {/* Layer 5: Depth vignette + spotlight */}
            <div className="ppbg__vignette" />
            <div className="ppbg__spotlight" />
          </div>
          <div className="premium-section-header">
            <h2 className="premium-title">
              Our Top 3 For FREE 
            </h2>
            <p className="premium-subtitle">
              {userState
                ? `Based on the expected value math of the bookmaker odds in ${userState}`
                : 'Highest expected value bets available right now'}
            </p>
          </div>
          <div className="player-prop-scroll">
            {rawBestBets.length === 0 && !showSkeletons && !authLoading ? (
              <RecalculatingState />
            ) : (
              <div className="premium-grid">
                {(showSkeletons || authLoading) ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="gc-skeleton-wrap">
                      <div className="gc-skeleton" style={{ height: '180px', width: '320px' }} />
                    </div>
                  ))
                ) : bestBets.length > 0 ? (
                  bestBets.map((bet, idx) => {
                    const cardKey = betKey(bet);

                  if (shouldLockPreviewItem({ accessState: bestBetsAccess, index: idx })) {
                    return (
                      <BestBetCard
                        key={cardKey}
                        bet={bet}
                        rank={idx + 1}
                        bookmakerIcons={HP_BOOKMAKER_ICONS}
                        formatMarket={hpFormatMarket}
                        fmtOdds={hpFmtOdds}
                        lockedOverlay={premiumBestBetsOverlay}
                      />
                    );
                  }

                  // Ranks 2–3 (idx 1–2): require a free account.
                  // Skip when override is active (subscription.isActive covers that).
                  if (idx >= 1 && !user && !subscription.isActive) {
                    return (
                      <BestBetCard
                        key={cardKey}
                        bet={bet}
                        rank={idx + 1}
                        bookmakerIcons={HP_BOOKMAKER_ICONS}
                        formatMarket={hpFormatMarket}
                        fmtOdds={hpFmtOdds}
                        lockedOverlay={{
                          title: 'Unlock Top Bets',
                          description: 'Create a free account to see all top bets.',
                          ctaLabel: 'Create Free Account',
                          ctaTo: '/register',
                        }}
                      />
                    );
                  }

                  return (
                    <BestBetCard
                      key={cardKey}
                      bet={bet}
                      rank={idx + 1}
                      bookmakerIcons={HP_BOOKMAKER_ICONS}
                      formatMarket={hpFormatMarket}
                      fmtOdds={hpFmtOdds}
                    />
                  );
                })
              ) : (
                <p className="no-picks">No bets available at this time.</p>
              )}
              </div>
            )}
          </div>
        </section>

        {/* ── What Do We Offer? ─────────────────────────────────────────── */}
        <section className="wwo-section">
          <div className="wwo-header">
            <h2 className="wwo-title">
              <span className="wwo-title__em">What Do We Offer?</span>
            </h2>
            <p className="wwo-subtitle">Everything you need to bet smarter, not harder.</p>
          </div>
          <div className="wwo-body">

            {/* Features list */}
            <div className="wwo-features">
              <ol className="hv2-features wwo-features__list">
                <li className="hv2-feature">
                  <span className="hv2-feature__num">01</span>
                  <div className="hv2-feature__body">
                    <span className="hv2-feature__title">Value Finder</span>
                    <span className="hv2-feature__desc">10,000+ bets scanned daily to find the best available prices, and identify the highest expected-value (+EV) betting opportunities across sportsbooks.</span>
                  </div>
                </li>
                <li className="hv2-feature">
                  <span className="hv2-feature__num">02</span>
                  <div className="hv2-feature__body">
                    <span className="hv2-feature__title">Promo Finder &amp; Calculator</span>
                    <span className="hv2-feature__desc">Turn sportsbook bonuses into guaranteed profit</span>
                  </div>
                </li>
                <li className="hv2-feature">
                  <span className="hv2-feature__num">03</span>
                  <div className="hv2-feature__body">
                    <span className="hv2-feature__title">Parlay Picker</span>
                    <span className="hv2-feature__desc">Evaluate true parlay edge vs. the house take</span>
                  </div>
                </li>
                <li className="hv2-feature hv2-feature--with-action">
                  <span className="hv2-feature__num">04</span>
                  <div className="hv2-feature__body">
                    <span className="hv2-feature__title">Website Tutorials &amp; Guided Sharp Betting</span>
                    <span className="hv2-feature__desc">Master the tools and concepts with beginner-friendly walkthroughs and real betting examples</span>
                  </div>
                  <Link
                    to="/academy"
                    className="hv2-btn hv2-btn--ghost hv2-feature__action-btn"
                  >
                    <span className="hv2-btn__play" aria-hidden="true">▶</span>
                    Watch Free Content
                  </Link>
                </li>
              </ol>
            </div>

            {/* Live EV Intelligence Panel */}
            <div className="wwo-panel">
              <div className="hv2-ev-panel">
                <div className="hv2-evp__chrome">
                  <span className="hv2-evp__dot hv2-evp__dot--r" />
                  <span className="hv2-evp__dot hv2-evp__dot--y" />
                  <span className="hv2-evp__dot hv2-evp__dot--g" />
                  <span className="hv2-evp__chrome-title">
                    {featuredBetDisplay?.tier === 3 ? 'Odds Analysis Example' : 'Live Odds Analysis'}
                  </span>
                  {featuredBetDisplay?.tier !== 3 && (
                    <span className="hv2-evp__live-badge">
                      <span className="hv2-evp__live-dot" />
                      LIVE
                    </span>
                  )}
                </div>
                {featuredBetDisplay ? (
                  <>
                    <div className="hv2-evp__meta">
                      {featuredBetDisplay.sportLabel && (
                        <span className="hv2-evp__meta-sport">{featuredBetDisplay.sportLabel}</span>
                      )}
                      <span className="hv2-evp__meta-sep">·</span>
                      <span className="hv2-evp__meta-market">{featuredBetDisplay.marketLabel}</span>
                      {featuredBetDisplay.gameTime && (
                        <>
                          <span className="hv2-evp__meta-sep">·</span>
                          <span className="hv2-evp__meta-time">{featuredBetDisplay.gameTime}</span>
                        </>
                      )}
                    </div>
                    <div className="hv2-evp__title">{featuredBetDisplay.betTitle}</div>
                    <div className="hv2-evp__ev">
                      <div className="hv2-evp__ev-glow" aria-hidden="true" />
                      <span className="hv2-evp__ev-num">+{featuredBetDisplay.evPct.toFixed(1)}%</span>
                      <span className="hv2-evp__ev-label">Expected Value Edge</span>
                    </div>
                    <div className="hv2-evp__compare">
                      <div className="hv2-evp__obox hv2-evp__obox--fair">
                        <span className="hv2-evp__obox-label">Fair Odds</span>
                        <span className="hv2-evp__obox-val">
                          {featuredBetDisplay.fairOdds != null ? hpFmtOdds(featuredBetDisplay.fairOdds) : '—'}
                        </span>
                        <span className="hv2-evp__obox-sub">True probability</span>
                      </div>
                      <div className="hv2-evp__connector">
                        <div className="hv2-evp__connector-line" />
                        <div className="hv2-evp__connector-badge">
                          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                            <circle cx="6" cy="6" r="5" stroke="#22C55E" strokeWidth="1.5"/>
                            <path d="M3.5 6h5M7 4l2 2-2 2" stroke="#22C55E" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Market Inefficiency
                        </div>
                      </div>
                      <div className="hv2-evp__obox hv2-evp__obox--best">
                        <span className="hv2-evp__obox-label">Best Available</span>
                        <span className="hv2-evp__obox-val hv2-evp__obox-val--best">
                          {hpFmtOdds(featuredBetDisplay.bestPrice)}
                        </span>
                        {featuredBetDisplay.bestBookLogo
                          ? <img src={featuredBetDisplay.bestBookLogo} alt={featuredBetDisplay.bestBook} className="hv2-evp__obox-logo" loading="lazy" width={34} height={20} />
                          : <span className="hv2-evp__obox-sub">{featuredBetDisplay.bestBook}</span>
                        }
                      </div>
                    </div>
                    {featuredBetDisplay.bookEntries.length > 0 && (
                      <div className="hv2-evp__books-wrap">
                        <div className="hv2-evp__books-hdr">Sportsbook Comparison</div>
                        <div className="hv2-evp__books">
                          {featuredBetDisplay.bookEntries.map(({ name, price, logo, isBest }) => (
                            <div key={name} className={`hv2-evp__chip${isBest ? ' hv2-evp__chip--best' : ''}`}>
                              {logo
                                ? <img src={logo} alt={name} className="hv2-evp__chip-logo" loading="lazy" width={30} height={18} />
                                : <span className="hv2-evp__chip-abbr">{name.slice(0, 2).toUpperCase()}</span>
                              }
                              <span className="hv2-evp__chip-odds">{hpFmtOdds(price)}</span>
                              {isBest && <span className="hv2-evp__chip-crown">&#9733;</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {featuredBetDisplay.bookEntries.length >= 2 && (
                      <div className="hv2-evp__spectrum">
                        <div className="hv2-evp__spec-hdr">
                          <span>Market Spread</span>
                          <span className="hv2-evp__spec-range">
                            {hpFmtOdds(featuredBetDisplay.minP)} – {hpFmtOdds(featuredBetDisplay.maxP)}
                          </span>
                        </div>
                        <div className="hv2-evp__spec-track">
                          <div className="hv2-evp__spec-fill" />
                          {featuredBetDisplay.bookEntries.map(({ name, price, isBest }) => {
                            const pct = featuredBetDisplay.range > 0
                              ? ((price - featuredBetDisplay.minP) / featuredBetDisplay.range) * 100
                              : 50;
                            return (
                              <span
                                key={name}
                                className={`hv2-evp__spec-dot${isBest ? ' hv2-evp__spec-dot--best' : ''}`}
                                style={{ left: `${pct}%` }}
                                title={`${name}: ${hpFmtOdds(price)}`}
                              />
                            );
                          })}
                        </div>
                        <div className="hv2-evp__spec-ends">
                          <span>Worst odds</span>
                          <span>Best odds</span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="hv2-evp__skeleton">
                    <div className="hv2-evp__skel hv2-evp__skel--meta" />
                    <div className="hv2-evp__skel hv2-evp__skel--title" />
                    <div className="hv2-evp__skel hv2-evp__skel--ev" />
                    <div className="hv2-evp__skel-compare">
                      <div className="hv2-evp__skel hv2-evp__skel--box" />
                      <div className="hv2-evp__skel hv2-evp__skel--box" />
                    </div>
                    <div className="hv2-evp__skel hv2-evp__skel--chips" />
                  </div>
                )}
              </div>
            </div>

          </div>
        </section>

        {/* ── State Sportsbooks / DFS / Trading Markets (SEO + platform discovery) ── */}
        <section className="ssp-section" aria-labelledby="ssp-heading">
          <div className="ssp-inner">
            <div className="ssp-panel">
              {/* Decorative layered background — purely visual, not content */}
              <div className="ssp-panel__bg" aria-hidden="true">
                <div className="ssp-panel__grid" />
                <div className="ssp-panel__glow ssp-panel__glow--1" />
                <div className="ssp-panel__glow ssp-panel__glow--2" />
              </div>

              <div className="ssp-panel__content">
                <h2 id="ssp-heading" className="ssp-title">
                  Sports Betting in {stateName}
                </h2>
                <p className="ssp-copy">
                  <strong>{stateName} has {statePlatforms.total} available betting platform{statePlatforms.total === 1 ? '' : 's'}</strong> — sportsbooks,
                  DFS apps, and trading markets that BoostYourBets tracks. We analyze {stateName} sportsbook odds across every platform below to help
                  you compare odds, spot pricing gaps, and identify positive expected value (+EV) opportunities.
                </p>
                <p className="ssp-copy ssp-copy--sub">
                  More {stateName} sportsbook apps mean more sportsbook odds to compare. BoostYourBets isn't another sportsbook — it's an odds
                  comparison and sports betting analytics tool built to help {stateName} bettors find better potential value across every platform
                  they already use.
                </p>

                <div className="ssp-categories">
                  {PLATFORM_CATEGORY_SECTIONS.map(cat => (
                    <details className="ssp-category" open key={cat.key}>
                      <summary className="ssp-category__summary">
                        <span className="ssp-category__title-wrap">
                          <h3 className="ssp-category__title">{cat.title}</h3>
                          <span className="ssp-info" tabIndex={0} role="button" aria-label={`What is ${cat.title}?`}>
                            <span className="ssp-info__glyph" aria-hidden="true">ⓘ</span>
                            <span className="ssp-info__tooltip" role="tooltip">{cat.info}</span>
                          </span>
                        </span>
                        <span className="ssp-category__meta">
                          <span className="ssp-category__count">{cat.items.length}</span>
                          <span className="ssp-category__chevron" aria-hidden="true" />
                        </span>
                      </summary>
                      <div className="ssp-category__body">
                        {cat.items.length > 0 ? (
                          <ul className="ssp-platform-list">
                            {cat.items.map(p => (
                              <li key={p.key} className="ssp-platform">
                                <Link
                                  to={`/promotions/${stateSlug}`}
                                  className="ssp-platform__link"
                                  aria-label={`View ${p.label} promotions in ${stateName}`}
                                >
                                  <img src={p.icon} alt="" className="ssp-platform__icon" aria-hidden="true" />
                                  <span className="ssp-platform__name">{p.label}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="ssp-empty">No {cat.title.toLowerCase()} currently tracked in {stateName}.</p>
                        )}
                      </div>
                    </details>
                  ))}
                </div>

                <div className="ssp-links">
                  <Link to={`/value-finder/${stateSlug}`} className="ssp-links__item">{stateName} Value Finder</Link>
                  <Link to={`/promotions/${stateSlug}`} className="ssp-links__item">Sportsbook Promotions</Link>
                  <Link to="/academy/ev-betting" className="ssp-links__item">What Is Expected Value?</Link>
                  <Link to="/calculators" className="ssp-links__item">Odds &amp; Implied Probability Calculators</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Sport Filter ───────────────────────────────────────────────── */}
        <div className="home-sport-filter">
          <button
            className={`hsf-btn${selectedHomeSport === 'upcoming' ? ' hsf-btn--active' : ''}`}
            onClick={() => setSelectedHomeSport('upcoming')}
          >
            Upcoming
          </button>
          {availableSports.map(sport => {
            const label = SPORT_LABELS[sport] ?? sport.split('_').pop().toUpperCase();
            // MLB override: always use the exact Odds API key regardless of what
            // the available-sports endpoint returns for this entry.
            const sportKey = label === 'MLB' ? 'baseball_mlb' : sport;
            return (
              <button
                key={sport}
                className={`hsf-btn${selectedHomeSport === sportKey ? ' hsf-btn--active' : ''}`}
                onClick={() => setSelectedHomeSport(sportKey)}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Secondary filter: individual soccer leagues, shown only when the
            consolidated Soccer group is selected. Options are derived from
            soccerSubSports (live API data), never hardcoded. */}
        {isSoccerModeActive && soccerSubSports.length > 0 && (
          <div className="home-sport-filter home-sport-filter--secondary" role="group" aria-label="Soccer league">
            <button
              className={`hsf-btn hsf-btn--sub${selectedHomeSport === SOCCER_GROUP_VALUE ? ' hsf-btn--active' : ''}`}
              onClick={() => setSelectedHomeSport(SOCCER_GROUP_VALUE)}
            >
              All Soccer
            </button>
            {soccerSubSports.map(league => {
              const label = SPORT_LABELS[league] ?? league.split('_').pop().toUpperCase();
              return (
                <button
                  key={league}
                  className={`hsf-btn hsf-btn--sub${selectedHomeSport === league ? ' hsf-btn--active' : ''}`}
                  onClick={() => setSelectedHomeSport(league)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* ── Game Cards: single horizontal row ─────────────────────────── */}
        <div className="home-cards-section">
          {!showGameCardSkeletons && homeSportGames.length === 0 ? (
            <RecalculatingState title="Scanning Today's Markets" />
          ) : (
            <div className="horizontal-scroll-container game-card-list">
              {showGameCardSkeletons
                ? Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="game-card-wrapper gc-skeleton-wrap">
                      <div className="gc-skeleton" />
                    </div>
                  ))
                : homeSportGames.map((game, index) => (
                    <div key={game.gameId} className="game-card-wrapper">
                      {index === 0 || isSubscribed ? (
                        <GameCard
                          markets={game.markets}
                          homeTeam={game.homeTeam}
                          awayTeam={game.awayTeam}
                          commenceTime={game.commenceTime}
                        />
                      ) : (
                        <GameCard
                          markets={game.markets}
                          homeTeam={game.homeTeam}
                          awayTeam={game.awayTeam}
                          commenceTime={game.commenceTime}
                          isBlurred={true}
                        />
                      )}
                    </div>
                  ))
              }
            </div>
          )}
        </div>
      </div>
    </>
  );
}
